import { Component } from "@kluntje/core";
import {
  removeClass,
  addClass,
  hasClass,
  find,
  getParent,
  findAll,
  onEvent,
  removeEvent,
  toggleClass,
} from "@kluntje/js-utils/lib/dom-helpers";
import { HIDDEN, ACTIVE } from "Helper/cssClasses";
import { getDocLocale } from "Helper/metaHelper";
import FraDateTimePicker from "Components/fra-e-datetime-picker/fra-e-datetime-picker";

class FRABookFlight extends Component {
  constructor() {
    super({
      ui: {
        roundtripFlight: ".fra-js-bookflight__roundtrip :-one",
        onewayFlight: ".fra-js-bookflight__oneway :-one",
        forkFlight: ".fra-js-bookflight__fork :-one",
        addForkRow: ".fra-js-book-flight__add-row :-one",
        removeForkRow: ".fra-js-book-flight__delete-row",
        formTag: ".fra-js-book-flight__form :-one",
        formSubmit: ".fra-js-book-flight__submit :-one",
        roadtripOnewayTab: ".fra-js-book-flight__roadtrip-oneway :-one",
        forkFlightTab: ".fra-js-book-flight__fork :-one",
        forkFlightDates: ".fra-js-book-flight__fork fra-e-datetime-picker",
        roadTripDatepickers: ".fra-js-book-flight__roadtrip-oneway .fra-e-datetime-picker__field",
        inputs: "input",
        inputPax: ".fra-js-form-field--pax__input :-one",
        numberInputs: ".fra-js-number-input",
        formFieldPax: ".fra-js-form-field--pax :-one",
        paxNumber: ".fra-js-number-input--pax :-one",
        paxChdNumber: ".fra-js-number-input--pax-chd :-one",
        paxInfNumber: ".fra-js-number-input--pax-inf :-one",
        paxFlyout: ".fra-js-form-field--pax__flyout :-one",
        paxSubmit: ".fra-js-form-field--pax__submit :-one",
        flightClass: ".fra-js-radio__input--class",
        paxError: ".fra-js-form-field--pax__error :-one",
        lang: ".fra-js-book-flight__lang :-one",
      },
      events: [
        {
          event: "change",
          target: "roundtripFlight",
          handler: "handleFlightChange",
        },
        {
          event: "change",
          target: "onewayFlight",
          handler: "handleFlightChange",
        },
        {
          event: "change",
          target: "forkFlight",
          handler: "handleFlightChange",
        },
        {
          event: "click",
          target: "addForkRow",
          handler: "addForkRow",
        },
        {
          event: "click",
          target: "removeForkRow",
          handler: "removeForkRow",
        },
        {
          event: "focus",
          target: "inputPax",
          handler: "openPaxFlyout",
        },
        {
          event: "click",
          target: "paxSubmit",
          handler: "onPaxSubmit",
        },
        {
          event: "change",
          target: "flightClass",
          handler: "handleFlightClass",
        },
        {
          event: "fra-number-input-changed",
          target: "numberInputs",
          handler: "handlePax",
        },
        {
          event: "click",
          target: "formSubmit",
          handler: "handleSubmit",
        },
        {
          event: "fra-e-datetime-picker:change",
          target: "forkFlightDates",
          handler: "handleForkDateChange",
        },
      ],
      initialStates: {
        paxFlyoutOpen: false,
        paxInputValue: "",
        totalPax: 1,
        pax: 1,
        paxChd: 0,
        paxInf: 0,
        flightClass: "Economy",
      },
      reactions: {
        totalPax: ["setInputPaxValue", "handlePaxError"],
        flightClass: ["setInputPaxValue"],
        paxFlyoutOpen: ["onPaxFlyoutChange"],
      },
    });
  }

  private readonly ROUNDTRIP_WAY_FLIGHT = "fra-m-book-flight--roundtrip-flight";
  private readonly ONE_WAY_FLIGHT = "fra-m-book-flight--oneway-flight";
  private readonly ONE_FORK_FLIGHT = "fra-m-book-flight--fork-flight";
  private readonly NUM_PAX = "fra-m-number-input--pax";
  private readonly NUM_PAX_CHD = "fra-m-number-input--pax-chd";
  private readonly NUM_PAX_INF = "fra-m-number-input--pax-inf";

  get i18nPaxInputText() {
    return !this.checkEmptyString(this.getAttribute("data-pax-input-text")!)
      ? this.getAttribute("data-pax-input-text")
      : "Passenger(s)";
  }

  /**
   * Set disable attrinute of the given element
   * @param {HTMLElement} item
   */
  setDisableAttribute(item: HTMLElement): void {
    item.setAttribute("disabled", "");
  }

  /**
   * Remove disable attrinute of the given element
   * @param {HTMLElement} item
   */
  removeDisableAttribute(item: HTMLElement): void {
    item.removeAttribute("disabled");
  }

  /**
   * Check string content and return true if the string is empty
   * @return {boolean}
   * @param {string} text
   */
  checkEmptyString(text: string): boolean {
    return text.trim() === "";
  }

  /**
   * Prevent sending empty parameters on form submit
   */
  handleSubmit(): void {
    const emptyInputs: HTMLInputElement[] = [...this.ui.inputs].filter((input: HTMLInputElement) =>
      this.checkEmptyString(input.value),
    );
    emptyInputs.forEach(input => this.setDisableAttribute(input));

    this.ui.formTag.submit();
  }

  /**
   * Web Component lifecycle hook
   */
  afterComponentRender(): void {
    this.setInputPaxValue();
    this.ui.lang.value = getDocLocale();
    this.getInputs(this.ui.forkFlightTab).forEach(item => this.setDisableAttribute(item as HTMLElement));
  }

  /**
   * Get all the inputs of a given parent
   * @param {HTMLElement} parent - html selector
   * @return {NodeList}
   */
  getInputs(parent: HTMLElement): NodeList {
    return findAll(parent, "input, fra-e-datetime-picker__field");
  }

  /**
   * Show differents forms when the user select a trip type like a tab widget.
   */
  handleFlightChange(): void {
    const { roundtripFlight, onewayFlight, forkFlight } = this.ui;
    const { ROUNDTRIP_WAY_FLIGHT, ONE_WAY_FLIGHT, ONE_FORK_FLIGHT } = this;

    this.classList.remove(ROUNDTRIP_WAY_FLIGHT, ONE_WAY_FLIGHT, ONE_FORK_FLIGHT);

    if (roundtripFlight.checked) {
      addClass(this, ROUNDTRIP_WAY_FLIGHT);
      this.getInputs(this.ui.roadtripOnewayTab).forEach(item => this.removeDisableAttribute(item as HTMLElement));
      this.getInputs(this.ui.forkFlightTab).forEach(item => this.setDisableAttribute(item as HTMLElement));
      // makes sure the end date is not before the start date when the user changed the start date in the oneway flight
      if (this.ui.roadTripDatepickers[1].value < this.ui.roadTripDatepickers[0].value) {
        this.ui.roadTripDatepickers[1].value = this.ui.roadTripDatepickers[0].value;
      }
    }
    if (onewayFlight.checked) {
      addClass(this, ONE_WAY_FLIGHT);
      this.getInputs(this.ui.roadtripOnewayTab).forEach(item => this.removeDisableAttribute(item as HTMLElement));
      this.getInputs(this.ui.forkFlightTab).forEach(item => this.setDisableAttribute(item as HTMLElement));
      // the return date should be hidden
      this.ui.roadTripDatepickers[1].disabled = true;
    }
    if (forkFlight.checked) {
      addClass(this, ONE_FORK_FLIGHT);
      this.getInputs(this.ui.forkFlightTab).forEach(item => this.removeDisableAttribute(item as HTMLElement));
      this.getInputs(this.ui.roadtripOnewayTab).forEach(item => this.setDisableAttribute(item as HTMLElement));
      this.ui.roadTripDatepickers[0].disabled = true;
      this.ui.roadTripDatepickers[1].disabled = true;
    }
  }

  handleForkDateChange(e: CustomEvent) {
    const target = e.target as FraDateTimePicker;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const forkFlightDates = Array.from(this.ui.forkFlightDates) as FraDateTimePicker[];
    const indexOfChangedRow = forkFlightDates.indexOf(target);
    // make sure date inputs for the flight rows after the currently changed row, are chronologically after tje current one
    // similar to how the https://flights.frankfurt-airport.com/ page works
    forkFlightDates.forEach((el, ix) => {
      if (ix > indexOfChangedRow && el.date < target.date) el.date = target.date;
    });
  }

  /**
   * In the fork flight view, remove flight row.
   * @param {event} event
   */
  removeForkRow(event: Event): void {
    const row = getParent(event.target as HTMLElement, ".fra-m-book-flight__fork-row");

    if (row) {
      row.classList.add(HIDDEN);
    }
    this.renderAddForkRow();
  }

  /**
   * In the fork flight view, add flight row.
   */
  addForkRow(): void {
    const rows = findAll(this, ".fra-m-book-flight__fork-row");

    for (let i = 0; i < rows.length; i++) {
      const row: any = rows[i];

      if (hasClass(row, HIDDEN)) {
        row.classList.remove(HIDDEN);
        break;
      }
    }
    this.renderAddForkRow();
  }

  /**
   * In the fork flight view, show the add flight row button. Don´t show the add button if you have four flights selected.
   */
  renderAddForkRow(): void {
    const rows = findAll(this, ".fra-m-book-flight__fork-row");
    const { addForkRow } = this.ui;

    for (let i = 0; i < rows.length; i++) {
      const row: any = rows[i];

      if (hasClass(row, HIDDEN)) {
        addForkRow.classList.remove(HIDDEN);
        break;
      } else {
        addForkRow.classList.add(HIDDEN);
      }
    }
  }

  /**
   * Function that open the passenger flyout
   */
  openPaxFlyout(): void {
    this.setState({ paxFlyoutOpen: true });
  }

  /**
   * Function that close the passenger flyout
   * @param {Event} e
   */
  closePaxFlyout(e?: Event): void {
    if (e && e.target && getParent(e.target as HTMLElement, ".fra-e-form-field--pax") !== null) return;
    this.setState({ paxFlyoutOpen: false });
  }

  onPaxFlyoutChange({ paxFlyoutOpen }: any) {
    toggleClass(this.ui.formFieldPax, ACTIVE, paxFlyoutOpen);

    if (paxFlyoutOpen) {
      onEvent(document.body, "click", this.closePaxFlyout, this);
    } else {
      removeEvent(document.body, "click", this.closePaxFlyout, this);
    }
  }

  onPaxSubmit(e?: Event): void {
    if (e !== undefined) e.preventDefault();
    this.setState({ paxFlyoutOpen: false });
  }

  /**
   * Set and format the input passenger with passenger number, flight class type, and store this value in the state
   */
  setInputPaxValue(): void {
    const { totalPax, flightClass } = this.state;
    const paxInputValue = `${totalPax} ${this.i18nPaxInputText}, ${flightClass}`;
    this.setState({ paxInputValue });
    this.ui.inputPax.value = paxInputValue;
  }

  /*
   * Store the flightclass based on radio selection
   */
  handleFlightClass(): void {
    let selectedFlightClassId: string | null = null;

    this.ui.flightClass.forEach((element: HTMLInputElement) => {
      if (element.checked) {
        selectedFlightClassId = element.getAttribute("id");
      }
    });

    if (selectedFlightClassId !== null) {
      this.setState({ flightClass: this.getInputLabel(selectedFlightClassId).innerHTML });
    }
  }

  /**
   * Update the passengers, children, and total passengers states, based on the input number change
   * @param {CustomEvent} event
   */
  handlePax(event: CustomEvent): void {
    if (hasClass(event.target as HTMLElement, this.NUM_PAX)) {
      this.setState({ pax: event.detail.value });
    }
    if (hasClass(event.target as HTMLElement, this.NUM_PAX_CHD)) {
      this.setState({ paxChd: event.detail.value });
    }
    if (hasClass(event.target as HTMLElement, this.NUM_PAX_INF)) {
      this.setState({ paxInf: event.detail.value });
    }

    this.setTotalPax(this.state.pax, this.state.paxChd, this.state.paxInf);
  }

  /**
   * Function for setting the total passenger state (totalPax)
   * @param {number} pax - adult passengers
   * @param {number} paxChd - child passenger
   * @param {number} paxInf - infant passenger
   */
  setTotalPax(pax: number, paxChd: number, paxInf: number): void {
    const totalPax = pax + paxChd + paxInf;
    this.setState({ totalPax });
  }

  handlePaxError(): void {
    const { paxError, paxSubmit, formSubmit } = this.ui;
    const paxChdErrorMessage = this.getAttribute("data-pax-chd-error") as string;
    const paxErrorMessage = this.getAttribute("data-pax-error") as string;

    if (this.state.totalPax > 9) {
      removeClass(paxError, HIDDEN);
      paxError.textContent = paxErrorMessage;
      this.setDisableAttribute(paxSubmit);
      this.setDisableAttribute(formSubmit);
    } else if (this.state.paxInf > this.state.pax) {
      removeClass(paxError, HIDDEN);
      paxError.textContent = paxChdErrorMessage;
      this.setDisableAttribute(paxSubmit);
      this.setDisableAttribute(formSubmit);
    } else {
      addClass(paxError, HIDDEN);
      this.removeDisableAttribute(paxSubmit);
      this.removeDisableAttribute(formSubmit);
    }
  }

  /**
   * Get the input label from 'id' and 'for' attribute
   * @param {String} id - id attribute of the input element and for attribute of the label
   * @return {HTMLLabelElement} - HTML Label Element
   */
  getInputLabel(id: string | null): HTMLLabelElement {
    const label = find(this, `label[for=${id}]`) as HTMLLabelElement;
    return label;
  }
}

customElements.define("fra-m-book-flight", FRABookFlight);

export default FRABookFlight;
